import styled from 'styled-components';
import { ReactNode } from 'react';
import { Body, Divider, Grid, Highlight, Stack, Typography, getToken, Tag } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import {
    EnterpriseBuilderParametersType,
    EnterpriseBuilderQueueItemDetail,
    QueueItemState,
} from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { LocalizedSmallParagraph } from '@mews-commander/ui';

interface EnterpriseInfoProps {
    queueItemDetail: EnterpriseBuilderQueueItemDetail;
    jobId: string;
    selectedEntities: string[];
}

const MetaDataItem = ({ data, labelTextKey }: { data: ReactNode; labelTextKey: Mews.TextKey }) => (
    <>
        <Grid columnGap="50" templateColumns="minmax(160px, min-content) 1fr">
            <LocalizedSmallParagraph variant="secondary" textKey={labelTextKey} />
            <Body size="small">{data}</Body>
        </Grid>
        <Divider />
    </>
);

const CodeWrapper = styled.pre`
    border: 0;
    border-radius: 24px;
    padding: 24px;
    font-size: 14px;
    font-family: inherit;
    font-weight: 400;
    color: ${getToken('textStaticSecondary')};
    background: ${getToken('backgroundAlertBasicSecondary')};
`;

export const EnterpriseInfo = ({ queueItemDetail, jobId, selectedEntities }: EnterpriseInfoProps) => {
    const data = queueItemDetail.inputData?.data;

    return (
        <>
            <Stack vertical spacing="150">
                <>
                    <MetaDataItem data={jobId} labelTextKey={TextKey.JobIdentifier} />
                    {data?.discriminator === EnterpriseBuilderParametersType.NewEnterprise ? (
                        <>
                            <MetaDataItem data={data.value.uniqueName} labelTextKey={TextKey.UniqueName} />
                            <MetaDataItem data={data.value.subscriptionId} labelTextKey={TextKey.SubscriptionId} />
                            <MetaDataItem data={data.value.chainId} labelTextKey={TextKey.ChainId} />
                            <MetaDataItem data={data.value.cityId} labelTextKey={TextKey.CityId} />
                        </>
                    ) : (
                        data && (
                            <>
                                <MetaDataItem
                                    data={data.value.enterpriseReferenceMapping.targetEntityId}
                                    labelTextKey={TextKey.PropertyIdentifier}
                                />
                            </>
                        )
                    )}
                    <MetaDataItem
                        data={
                            <Stack spacing="100" wrap>
                                {selectedEntities.map((item) => (
                                    <Tag variant="basicSubtle">{item}</Tag>
                                ))}
                            </Stack>
                        }
                        labelTextKey={TextKey.Settings}
                    />
                </>
            </Stack>
            {queueItemDetail.state === QueueItemState.Success && (
                <Stack vertical spacing="200">
                    <Highlight size="medium">{lt('CodeToCopy')}</Highlight>
                    {data?.discriminator === EnterpriseBuilderParametersType.ExistingEnterprise && (
                        <Typography textStyle="bodyMedium" variant="secondary" as="div">
                            {lt('EnterpriseBuilderProExistingPropertyHint')}
                        </Typography>
                    )}
                    <CodeWrapper>{JSON.stringify(queueItemDetail.outputData, null, 2)}</CodeWrapper>
                </Stack>
            )}
        </>
    );
};
